import React, { useCallback, useState } from "react";

import Image from "next/image";
import Link from "next/link";
import CloseIcon from "public/icons/close.svg";
import FavoriteActiveIcon from "public/icons/favorite-active.svg";
import GradeActiveIcon from "public/icons/grade-active.svg";
import RightCaret from "public/icons/right-caret.svg";
import StarIcon from "public/icons/star-icon.svg";
import IconBgLeft from "public/icons/sweet_or_hot_bg_cliped_left.svg";
import IconBgRignt from "public/icons/sweet_or_hot_bg_cliped_right.svg";
import NoPhotoImg from "public/images/no-photo.png";

import { Skeleton } from "@mui/material";

import { Button } from "@/components/form/Button/Button";
import { Old_Button } from "@/components/form/Button/Old_Button";
import { useOpenModal } from "@/components/modal/utils/openModal";
import { LadyNameVerified } from "@/components/ui/lady-name-verified/LadyNameVerified";
import { SvgIcon } from "@/components/ui/svg-icon/SvgIcon";
import { useLadyIdContext } from "@/context/LadyId.context";
import { clsxm } from "@/utils/tailwindMerge";

import { useCurrentSweetOrHotLady, useSweetOrHotLadies } from "./hooks";

type PropTypes = {
  withHandleDrawerClose: (fn?: () => void) => () => void;
};

export const SweetOrHotBanner: React.FC<PropTypes> = ({
  withHandleDrawerClose,
}) => {
  const { setLadyId } = useLadyIdContext();
  const { data: pagesData, isFetching, refetch } = useSweetOrHotLadies();

  const [currentIndex, setCurrentIndex] = useState(0);

  const { currentLady, nextLady } = useCurrentSweetOrHotLady({
    data: pagesData?.requestDTO.dataMap.result || [],
    fetchNextPage: refetch,
    isFetching,
    currentIndex,
    setCurrentIndex,
  });

  const {
    openModal: openModalWithDarkerBackdrop,
    removeOne: removeOneWithDarkerBackdrop,
  } = useOpenModal({ darkerBackdrop: true });
  const {
    openModal: openModalWithoutDarkerBackdrop,
    removeOne: removeOneWithoutDarkerBackdrop,
  } = useOpenModal({ darkerBackdrop: false });

  const createOpenSweetOrHotModal = (imgUrl: string) => () =>
    openModalWithDarkerBackdrop("SWEET_OR_HOT_MODAL", {
      onClose: removeOneWithDarkerBackdrop,
      imgUrl,
    });
  const openModalWithHeader = useCallback(
    (header: string, isOmitHeaderForWord: boolean) =>
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        currentLady &&
          openModalWithoutDarkerBackdrop("PROFILE_ACTION_BUTTONS_MODAL", {
            onClose: removeOneWithoutDarkerBackdrop,
            header,
            isOmitHeaderForWord,
            ladyDtoParam: {
              id: currentLady.id,
              firstName: currentLady.firstName,
              lastName: currentLady.lastName,
              isVideochatAvailable: currentLady.isVideochatAvailable,
              isBirthdaySoon: currentLady.isBirthdaySoon,
              isNewProfile: currentLady.isNewProfile,
              from: currentLady.from,
              hasIntroVideo: currentLady.hasIntroVideo,
              isGiftsAvailable: currentLady.isGiftsAvailable,
              photoSrcs: currentLady.photoSrcs,
            },
          });
      },
    [
      currentLady,
      openModalWithoutDarkerBackdrop,
      removeOneWithoutDarkerBackdrop,
    ],
  );

  const onClickHandler = (header: string) => {
    return (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      openModalWithHeader(header, true)(e);
      currentLady && setLadyId(currentLady.id.toString());
    };
  };

  const SkeletonPlaceholder = useCallback(() => {
    return (
      <Skeleton
        variant="rectangular"
        component={"div"}
        className="h-[361px] block"
      />
    );
  }, []);

  type StaticAssetWithSrc = {
    src: string;
  };
  // TODO: declare interface for StaticAsset for specific path (ignore public/icons)
  const picturePlug = (NoPhotoImg as StaticAssetWithSrc).src;

  const imgSrc = currentLady?.photoSrcs.large || picturePlug;

  const imgThumbnailsrc = currentLady?.photoSrcs.medium || picturePlug;

  const imageRef = React.useRef<HTMLImageElement>(null);

  const handleImageError = () => {
    if (imageRef.current) {
      imageRef.current.src = picturePlug;
    }
  };

  return (
    <div className="cursor-pointer" data-testid="sweetOrHot">
      {isFetching && !currentLady ? (
        <SkeletonPlaceholder />
      ) : !isFetching && !!currentLady ? (
        <>
          <div
            className="h-[310px] w-full rounded-lg relative overflow-clip"
            onClick={createOpenSweetOrHotModal(imgSrc)}
          >
            <Image
              src={imgThumbnailsrc}
              alt={`${currentLady.firstName} from ${currentLady.from} - ID ${currentLady.id}`}
              ref={imageRef}
              onError={handleImageError}
              fill
              className="object-cover"
              unoptimized
            />
            <div
              className={clsxm(
                "absolute top-0 py-2 text-white uppercase font-bold text-center w-full",
                "bg-gradient-to-b from-black to-transparent",
              )}
            >
              <span>Sweet or Hot?</span>
            </div>
            <div className="absolute bottom-4 h-1/5 w-full flex justify-center">
              <div className="flex flex-row justify-center items-center">
                {/* TODO: remove condition and the code after ":" in ternar operator
                 when old design of sweetOrHot banner concidered as not needed anymore */}
                {/* {isNewLeftbarDesign ? ( */}
                {true ? (
                  <>
                    <Button
                      variant="rounded"
                      className="relative mr-[-3px] bg-white hover:bg-white hover:opacity-[85%] transition-all"
                      onClick={onClickHandler("to Continue")}
                      aria-label="Sweet or Hot - Add lady to favorites"
                    >
                      <StarIcon />
                    </Button>
                    <Button
                      variant="rounded"
                      className="z-[1] w-10 h-10 bg-white hover:bg-white hover:opacity-[85%] transition-all"
                      onClick={onClickHandler("to Continue")}
                      aria-label="Sweet or Hot - Like lady"
                    >
                      <SvgIcon className="w-7 h-7">
                        <FavoriteActiveIcon />
                      </SvgIcon>
                    </Button>
                    <Button
                      variant="rounded"
                      className="relative ml-[-3px] bg-white  hover:bg-white hover:opacity-[85%] transition-all"
                      onClick={nextLady}
                      aria-label="Sweet or Hot - Next lady"
                    >
                      <RightCaret />
                    </Button>
                  </>
                ) : (
                  <>
                    <Old_Button
                      variant="icon"
                      className="relative mr-[-3px] hover:opacity-[85%] transition-all"
                      onClick={nextLady}
                      aria-label="Sweet or Hot - Next lady"
                    >
                      <SvgIcon width={40} height={40} className="absolute">
                        <IconBgLeft />
                      </SvgIcon>
                      <SvgIcon className="z-[1]" color="grey">
                        <CloseIcon />
                      </SvgIcon>
                    </Old_Button>
                    <Old_Button
                      variant="icon"
                      className="w-10 h-10 bg-white hover:bg-white hover:opacity-[85%] transition-all"
                      onClick={nextLady}
                      aria-label="Sweet or Hot - Like lady"
                    >
                      <SvgIcon className="w-7 h-7">
                        <FavoriteActiveIcon />
                      </SvgIcon>
                    </Old_Button>
                    <Old_Button
                      variant="icon"
                      className="relative ml-[-3px] hover:opacity-[85%] transition-all"
                      onClick={nextLady}
                      aria-label="Sweet or Hot - Add lady to favorites"
                    >
                      <SvgIcon width={40} height={40} className="absolute">
                        <IconBgRignt />
                      </SvgIcon>
                      <SvgIcon className="z-[1]" color="grey">
                        <GradeActiveIcon />
                      </SvgIcon>
                    </Old_Button>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <LadyNameVerified
              iconHeight={20}
              iconWidth={20}
              iconClassName="mb-1"
              className="font-bold text-base"
              typographyComponent="span"
            >
              <Link
                href={`/lady/id${currentLady.id}`}
                onClick={withHandleDrawerClose(() => refetch())}
                prefetch={false}
              >
                {currentLady.firstName}
              </Link>
            </LadyNameVerified>
            <span
              data-testid="ladyLocation"
              className="text-sm text-grey1-700 cursor-text"
            >
              {currentLady.from}
            </span>
          </div>
        </>
      ) : (
        <SkeletonPlaceholder />
      )}
    </div>
  );
};
